import { IconButton, TableOrderByType, Tooltip } from '@elipssolution/harfang';
import { mdiClose } from '@mdi/js';
import Icon from '@mdi/react';
import { Checkbox, Stack, Typography, styled } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';

import TagAutocomplete from './TagAutocomplete';
import useFormat from '../../../../hooks/useFormat';
import { formatFileSize } from '../../../../src/utils/file';
import { TagType } from '../../types/tag';

const StyledTypography = styled(Typography)(() => ({
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	display: '-webkit-box',
	WebkitLineClamp: '2',
	WebkitBoxOrient: 'vertical',
	wordBreak: 'break-word',
}));

export type DocumentType = {
	file: File;
	tag?: TagType;
};

const DocumentItemWrapper = styled('div')(({ theme: { palette, spacing } }) => ({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	gap: spacing(2),
	padding: spacing(0, 1.5),

	'&.error': {
		backgroundColor: `${palette.error.main}1A`,
	},

	'& > div': {
		'&:first-of-type': {
			display: 'flex',
			alignItems: 'center',
		},

		'&:last-of-type': {
			minWidth: 200,
			display: 'flex',
			alignItems: 'center',
			gap: spacing(1),

			'& > div': {
				flex: 1,
			},
		},
	},
}));

type DocumentItemProps = {
	document: DocumentType;
	isSelected: boolean;
	onDelete: (file: File) => void;
	onSelection: (file: File, selected: boolean) => void;
	onTagChange: (document: DocumentType) => void;
	tagsDataSource: (
		limit: number,
		offset: number,
		search?: string,
		orderBy?: TableOrderByType<TagType>,
	) => Promise<{
		count: number;
		items: TagType[];
	}>;
	withSelection: boolean;
};

const DocumentItem = ({
	document: { file, tag },
	isSelected,
	onDelete,
	onSelection,
	onTagChange,
	tagsDataSource,
	withSelection,
}: DocumentItemProps) => {
	const { isMobileFormat } = useFormat();

	const { name, size } = file;

	const textElementRef = useRef<HTMLSpanElement>(null);

	const [isFilenameOverflowing, setIsFilenameOverflowing] = useState(false);

	const handleSelection = useCallback(() => onSelection(file, !isSelected), [onSelection, file, isSelected]);

	const handleTagChange = useCallback(
		(value?: TagType) =>
			onTagChange({
				file,
				tag: value,
			}),
		[onTagChange, file],
	);

	const handleDeleteButtonClick = useCallback(() => onDelete(file), [onDelete, file]);

	useEffect(() => {
		if (textElementRef.current) {
			setIsFilenameOverflowing(textElementRef.current.scrollHeight > textElementRef.current.clientHeight);
		}
	}, []);

	return (
		<DocumentItemWrapper>
			<div>
				{withSelection && <Checkbox onChange={handleSelection} checked={isSelected} />}

				<Stack>
					<Tooltip content={isFilenameOverflowing ? name : undefined}>
						<StyledTypography ref={textElementRef} variant={isMobileFormat ? 'body2' : 'body1'} title={name}>
							{name}
						</StyledTypography>
					</Tooltip>
					<Typography variant="caption">{formatFileSize(size)}</Typography>
				</Stack>
			</div>

			<div>
				<TagAutocomplete dataSource={tagsDataSource} onChange={handleTagChange} value={tag} />

				<IconButton onClick={handleDeleteButtonClick} size="small">
					<Icon path={mdiClose} size="18px" />
				</IconButton>
			</div>
		</DocumentItemWrapper>
	);
};

export default DocumentItem;
